import React from 'react';
import PageHelmet from '../../../components/shared/PageHelmet';
import NavHeader from '../../../components/NavHeader/NavHeader';
import MainFooter from '../../../components/MainFooter/MainFooter';
import IndustryHero from '../../../components/IndustryHero/IndustryHero';
import IndustryChallenge from '../../../components/IndustryChallenge/IndustryChallenge';
import IndustryProductSpotlight from '../../../components/IndustryProductSpotlight/IndustryProductSpotlight';
import IndustryTechStack from '../../../components/IndustryTechStack/IndustryTechStack';
import OurExpertise from '../../../components/OurExpertise/OurExpertise';
import KeyFeatures from '../../Offerings/KeyFeatures/KeyFeatures';
import CommonCtaArea from '../../../components/CommonCtaArea/CommonCtaArea';
import { GraduationCap, Users, ClipboardCheck, BarChart3, FileCheck, Smartphone, Globe, Terminal, Database, PaintBucket, BookOpen, LayoutDashboard, MonitorPlay, ClipboardList, TrendingUp } from 'lucide-react';

const ChallengeItems = [
   'Courses',
   'Students',
   'Instructors',
   'Learning content',
   'Assessments',
   'Assignments',
   'Progress',
   'Communication',
   'Certifications',
   'Analytics',
];

const WhatWeBuildData = [
   { id: 1, icon: <GraduationCap size="64" strokeWidth='1' />, title: 'Learning Platforms', content: 'Centralized platforms for managing digital learning.' },
   { id: 2, icon: <Users size="64" strokeWidth='1' />, title: 'Student Portals', content: 'Give learners access to courses, content, assessments, and progress.' },
   { id: 3, icon: <ClipboardCheck size="64" strokeWidth='1' />, title: 'Instructor Platforms', content: 'Help instructors create courses, manage learners, and monitor performance.' },
   { id: 4, icon: <BarChart3 size="64" strokeWidth='1' />, title: 'Learning Analytics', content: 'Provide visibility into learner progress and engagement.' },
   { id: 5, icon: <FileCheck size="64" strokeWidth='1' />, title: 'Assessment Platforms', content: 'Support quizzes, assignments, evaluations, and results.' },
   { id: 6, icon: <Smartphone size="64" strokeWidth='1' />, title: 'Mobile Learning', content: 'Create learning experiences across smartphones and tablets.' },
];

const TechData = [
   { icon: <Globe size={32} strokeWidth={1.5} />, name: 'React', descriptor: '' },
   { icon: <Terminal size={32} strokeWidth={1.5} />, name: 'Python', descriptor: '' },
   { icon: <Database size={32} strokeWidth={1.5} />, name: 'PostgreSQL', descriptor: '' },
   { icon: <PaintBucket size={32} strokeWidth={1.5} />, name: 'UI/UX', descriptor: '' },
   { icon: <Smartphone size={32} strokeWidth={1.5} />, name: 'React Native / Flutter', descriptor: 'Where mobile learning is required' },
];

const SolutionsData = [
   { id: 1, icon: <GraduationCap size="42" strokeWidth='1' />, title: 'Learning Management Systems', content: '' },
   { id: 2, icon: <BookOpen size="42" strokeWidth='1' />, title: 'E-Learning Platforms', content: '' },
   { id: 3, icon: <Users size="42" strokeWidth='1' />, title: 'Student Portals', content: '' },
   { id: 4, icon: <LayoutDashboard size="42" strokeWidth='1' />, title: 'Instructor Dashboards', content: '' },
   { id: 5, icon: <MonitorPlay size="42" strokeWidth='1' />, title: 'Online Course Platforms', content: '' },
   { id: 6, icon: <FileCheck size="42" strokeWidth='1' />, title: 'Assessment Systems', content: '' },
   { id: 7, icon: <ClipboardList size="42" strokeWidth='1' />, title: 'Training Management Platforms', content: '' },
   { id: 8, icon: <TrendingUp size="42" strokeWidth='1' />, title: 'Learning Analytics', content: '' },
];

const Education = () => {
   return (
      <>
         <PageHelmet
            pageTitle="Education Software Solutions"
            description="Modern technology for schools, training organizations, educators, institutions, and EdTech businesses — building learning platforms that are more accessible, measurable, and engaging."
            keywords="Education Software Development, Learning Management System Development, Harsoft Solutions"
            path="/industries/education"
         />

         <NavHeader />
         <main>
            <IndustryHero
               title="Build Better Digital Learning Experiences"
               intro="Modern technology for schools, training organizations, educators, institutions, and EdTech businesses."
               description="Harsoft creates learning platforms that make education more accessible, measurable, and engaging."
               primaryCta={{ label: 'Build Your Education Platform', to: '/contact' }}
            />

            <IndustryChallenge
               title="Education Challenges"
               intro="Education organizations need to manage:"
               items={ChallengeItems}
               outro="A connected digital platform can simplify these workflows."
            />

            <OurExpertise
               title="What We Build"
               subtitle="Solutions Built for Education Organizations"
               content="From learning platforms to instructor tools, we build the systems education organizations run on."
               OurExpertiseData={WhatWeBuildData}
            />

            <IndustryTechStack items={TechData} />

            <IndustryProductSpotlight
               name="LMS"
               tagline="Learning Management System"
               description="A centralized platform for managing:"
               bullets={['Courses', 'Learners', 'Instructors', 'Content', 'Assessments', 'Progress', 'Reports', 'Learning activities']}
               ctaLabel="Explore LMS"
               ctaLink="/contact"
            />

            <KeyFeatures KeyFeaturesData={SolutionsData} title="Education Solutions We Can Build" />

            <CommonCtaArea
               title={<>Have an Education Platform Idea?</>}
               subtitle="Let's create a learning experience that works for students, instructors, and administrators."
               buttonText="Build Your LMS"
               buttonLink="/contact"
            />
         </main>
         <MainFooter />
      </>
   );
};

export default Education;
